import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AdmissionsTrendChart } from "@/components/dashboard/AdmissionsTrendChart";
import { DashboardKpiCard } from "@/components/dashboard/DashboardKpiCard";
import { DashboardNav } from "@/components/dashboard/DashboardNav";
import { RevenueSplitChart } from "@/components/dashboard/RevenueSplitChart";
import { RotationBarChart } from "@/components/dashboard/RotationBarChart";
import { PageHeader } from "@/components/PageHeader";
import { buildOperationalDashboardAnalytics } from "@/core/analytics/operational-analytics";
import { routes } from "@/router/all_routes";

function IconPatients() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconBed() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M2 9V19a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V9M2 9V7a2 2 0 0 1 2-2h6v4M2 9h20"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconQueue() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M4 6h16M4 12h10M4 18h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconTimer() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="13" r="8" stroke="currentColor" strokeWidth="2" />
      <path d="M12 9v4l2 2M9 3h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconEmergency() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function OperationalDashboardPage() {
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const bump = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:admissions", bump);
    window.addEventListener("hospitol:clinical-prescriptions", bump);
    window.addEventListener("hospitol:hospitalization", bump);
    window.addEventListener("storage", bump);
    return () => {
      window.removeEventListener("hospitol:admissions", bump);
      window.removeEventListener("hospitol:clinical-prescriptions", bump);
      window.removeEventListener("hospitol:hospitalization", bump);
      window.removeEventListener("storage", bump);
    };
  }, []);

  const analytics = useMemo(() => {
    void refreshKey;
    return buildOperationalDashboardAnalytics();
  }, [refreshKey]);

  const todayLabel = new Date().toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div className="ph-dashboard ph-dashboard--operations">
      <PageHeader
        title="Performance opérationnelle"
        description="Rotations, flux patients, occupation et files d'attente — vue temps réel de l'activité."
      />

      <section className="ph-dashboard-hero ph-dashboard-hero--compact mb-4">
        <div className="ph-dashboard-hero-content">
          <p className="ph-dashboard-hero-kicker">Tableau de bord opérationnel — ITaaS Hospitol</p>
          <p className="ph-dashboard-hero-date mb-0">{todayLabel}</p>
        </div>
        <div className="ph-dashboard-hero-actions">
          <DashboardNav />
        </div>
      </section>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-4 col-xl">
          <DashboardKpiCard
            label="Patients du jour"
            value={String(analytics.patientsToday)}
            hint={`${analytics.admissionsToday} admission(s) · ${analytics.emergencyToday} urgence(s)`}
            icon={<IconPatients />}
            accent="teal"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <Link to={routes.admissions} className="text-decoration-none d-block h-100">
            <DashboardKpiCard
              label="Orientations"
              value={String(analytics.orientedToday)}
              hint={`${analytics.pendingBillingToday} en comptabilité`}
              icon={<IconQueue />}
              accent="emerald"
            />
          </Link>
        </div>
        <div className="col-6 col-md-4 col-xl">
          <DashboardKpiCard
            label="Délai orientation"
            value={
              analytics.avgOrientationMinutes !== null
                ? `${analytics.avgOrientationMinutes} min`
                : "—"
            }
            hint="Moyenne du jour (orientés)"
            icon={<IconTimer />}
            accent="violet"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <Link to={routes.hospitalization} className="text-decoration-none d-block h-100">
            <DashboardKpiCard
              label="Occupation lits"
              value={`${analytics.bedOccupancyPct} %`}
              hint={`${analytics.occupiedBeds} / ${analytics.totalBeds} lits`}
              icon={<IconBed />}
              accent="teal"
            />
          </Link>
        </div>
        <div className="col-6 col-md-4 col-xl">
          <Link to={routes.emergency} className="text-decoration-none d-block h-100">
            <DashboardKpiCard
              label="Urgences"
              value={String(analytics.emergencyToday)}
              hint="Passages du jour"
              icon={<IconEmergency />}
              accent="amber"
            />
          </Link>
        </div>
        <div className="col-6 col-md-4 col-xl">
          <DashboardKpiCard
            label="Files en attente"
            value={String(
              analytics.pharmacyQueue + analytics.labPending + analytics.imagingPending,
            )}
            hint={`Pharma ${analytics.pharmacyQueue} · Labo ${analytics.labPending} · Imagerie ${analytics.imagingPending}`}
            icon={<IconQueue />}
            accent="amber"
          />
        </div>
      </div>

      <div className="row g-3 mb-4 ph-split-workspace">
        <div className="col-lg-7">
          <div className="ph-invoice-sheet ph-dash-card h-100">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3 ph-split-detail-head">
              <div>
                <h2 className="h5 mb-1">Flux patients — 7 jours</h2>
                <p className="text-muted small mb-0">Admissions vs urgences par jour</p>
              </div>
              <Link to={routes.admissions} className="btn btn-sm btn-outline-primary">
                File accueil
              </Link>
            </div>
            <AdmissionsTrendChart data={analytics.admissionsLast7Days} />
          </div>
        </div>
        <div className="col-lg-5">
          <div className="ph-invoice-sheet ph-dash-card h-100">
            <h2 className="h5 mb-1">Triage urgences — aujourd&apos;hui</h2>
            <p className="text-muted small mb-3">P1 critique · P2 urgent · P3 stable</p>
            <RevenueSplitChart
              data={analytics.triageToday}
              emptyLabel="Aucune urgence enregistrée aujourd'hui."
              valueFormatter={(v) => `${v} passage(s)`}
            />
          </div>
        </div>
      </div>

      <div className="row g-3 ph-split-workspace">
        <div className="col-lg-7">
          <div className="ph-invoice-sheet ph-dash-card h-100">
            <h2 className="h5 mb-1">Rotations par service</h2>
            <p className="text-muted small mb-3">Orientations des 7 derniers jours</p>
            <RotationBarChart data={analytics.destinationRotation} />
          </div>
        </div>
        <div className="col-lg-5">
          <div className="ph-invoice-sheet ph-dash-card h-100">
            <h2 className="h5 mb-1">Mix canal — mois</h2>
            <p className="text-muted small mb-3">Admissions classiques vs urgences</p>
            <RevenueSplitChart
              data={analytics.channelMixMonth}
              emptyLabel="Aucun passage ce mois."
              valueFormatter={(v) => `${v} passage(s)`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
